// Client-side step-up for a sensitive action (US-1.4, audit-privacy-api.md §6).
//
// The flow is *reactive*: the caller runs the action; if the API answers with a step-up
// challenge (401 `step_up_required`, surfaced by lib/api as StepUpRequiredError), it asks the
// user to re-authenticate and then retries. This module hides the one environment split:
//   - SSO configured → a real Keycloak re-auth redirect (prompt=login) mints a fresh auth_time;
//     the page reloads on return holding a fresh token, so the retried action passes step-up.
//   - dev / E2E placeholder session (no IdP) → there is no credential to re-challenge, so step-up
//     resolves in-page and the retry carries an explicit assertion header (see STEP_UP_HEADER).
// The API remains the source of truth: the backend always checks the token's auth_time (§6); the
// header is only meaningful where no token exists at all.

import i18n from "../i18n";
import { StepUpRequiredError } from "../lib/api";
import { beginStepUp, isSsoConfigured } from "./oidc";
import { getAccessToken, getIdToken } from "./tokenStore";
import { fetchIdpHint } from "./tenantBranding";
import { storedSession } from "./session";
import { reachedByMembership, selectedTenantSlug } from "./tenantSelection";
import { resolveTenantSlug } from "../config";

/**
 * Header a retried sensitive action carries once step-up has been satisfied in the SSO-less
 * dev/E2E mode. In production the fresh token (auth_time) is authoritative and this is redundant.
 */
export const STEP_UP_HEADER = "X-Clms-Step-Up";
export const STEP_UP_HEADER_VALUE = "satisfied";

/** Header record to merge into a sensitive request once step-up is satisfied. */
export function stepUpHeaders(): Record<string, string> {
  return { [STEP_UP_HEADER]: STEP_UP_HEADER_VALUE };
}

/** Whether an error is the step-up challenge (the action needs a fresh re-auth, not a new login). */
export function isStepUpRequired(err: unknown): err is StepUpRequiredError {
  return err instanceof StepUpRequiredError;
}

/**
 * Perform step-up so the caller can retry the sensitive action.
 * - Returns `true` when step-up is satisfied in-page (dev/E2E) and the caller should retry now.
 * - Never returns when SSO is configured: it redirects to Keycloak for a fresh re-auth and the
 *   page navigates away; the caller re-initiates the action after the callback returns.
 *
 * `returnTo` is where to land after the redirect; `locale` is carried into the IdP (ui_locales).
 */
export async function performStepUp(returnTo: string, locale: string): Promise<boolean> {
  // A redirect-based re-auth only makes sense for a real OIDC session — the one whose auth_time the
  // backend re-checks. Gate on the *id token*, not just any stored bearer: only an OIDC login holds an
  // id token, so this is what distinguishes a Keycloak-backed session from a placeholder one. The
  // opaque-bearer sessions (external email-link, dev, E2E staff harness) carry NO id token — there is no
  // IdP credential to re-challenge — so step-up is satisfied in-page and the retry asserts it via header.
  if (isSsoConfigured() && getIdToken()) {
    // WHICH TENANT this re-auth is for, which on this leg the URL often cannot say. The slug is in the
    // address only on the tenant-addressed PRE-SESSION pages (`/t/<slug>/login`) and on a per-tenant
    // subdomain; every in-app route is apex-only (`app/router.tsx`), so without per-tenant subdomains —
    // localhost and the E2E harness among them — `resolveTenantSlug` answers null for exactly the pages a
    // sensitive action is taken from. Two fallbacks behind it, in this order: the person's own tenant
    // SELECTION (ADR-0018 §6.3 — the tenant they are actually working in, which on the apex host is the
    // only thing that knows), and then the session, which carried the sign-in's slug through the PKCE
    // round trip.
    const urlSlug = resolveTenantSlug();
    const tenant = urlSlug ?? selectedTenantSlug() ?? storedSession()?.tenantSlug ?? null;

    // ADR-0018 §6.3 — STEP-UP FOLLOWS WHO THE PERSON IS, NOT WHICH TENANT THEY ARE IN, and this is the one
    // line where that distinction has teeth.
    //
    //   * A customer's OWN person in a federated tenant must carry the hint (ADR-0014 §12 C-6): this
    //     re-auth forces a fresh credential prompt, their credential is at their employer's directory and
    //     not in this realm, and without the hint Keycloak would ask them for a realm password they have
    //     never had — the sensitive action would be unreachable for exactly the people a customer IdP
    //     exists to serve.
    //   * An Incedo consultant inside that same federated tenant must NOT. They reached it by a MEMBERSHIP,
    //     they are realm-local, and the hint would push them into the customer's broker — where they have
    //     no account and where the realm's `CLMS first broker login` refuses them rather than linking
    //     (TenantIdpFederation). Their first sign-in carried no hint for this reason (§6.1, the tenant-less
    //     entry), and a step-up that sent one would make every sensitive action in the customer's tenant
    //     unreachable for them alone — silently, since nothing else about the flow changes.
    //
    // So the question asked is about the ACCOUNT's route into this tenant and not about the tenant's
    // federation: `reachedByMembership` is true exactly when a membership is what put the person here.
    const idpHint = reachedByMembership(urlSlug) ? null : await fetchIdpHint(tenant);
    await beginStepUp({ tenant, returnTo, locale, idpHint });
    return false; // unreachable in practice — the redirect above navigates away
  }
  return true;
}

/**
 * ADR-0021 §6.2 — the SESSION's second factor is missing, so re-authenticate once.
 *
 * Registered as `setSecondFactorRequiredHandler` (src/auth/session.tsx) and reached from any 401 the
 * door answers a privileged session with. It is {@link performStepUp} with two differences, and both are
 * the difference between "this action needs a fresh auth" and "this session needs a factor":
 *
 *   * NOTHING IS RETRIED. There is no action to retry — the challenge arrived on whatever read a page
 *     happened to make — so the redirect IS the whole answer. The page reloads on return holding a token
 *     that carries the factor, and the reads it makes then succeed.
 *   * IT RUNS ONCE. A page mounting fires several requests and every one of them is refused, so without
 *     the latch below each would start its own redirect. `window.location.assign` would simply let the
 *     last one win, but the PKCE state is written to sessionStorage first (`beginStepUp`) and the
 *     verifier of a later call would overwrite the one the navigating request will come back with — a
 *     callback that cannot complete its own token exchange.
 *
 * No new authorization parameter, which is §6.2's point: the existing re-auth already does the work.
 * `prompt=login` + `max_age=0` forces a fresh credential prompt, and because the grant surface gave the
 * account the `requires-2fa` realm role (§6.2(a)) that prompt runs the realm's conditional subflow and
 * asks for a factor. No `acr_values` is sent, for §6.1 A-4's measured reason.
 *
 * **What this redirect does for somebody who has NO factor, measured and not assumed:** it enrols one —
 * but it takes TWO of them, and that is why this function must stay re-enterable.
 *
 * ADR-0021 §6 expected the realm to enrol at the challenge because its second-factor executions carried
 * `userSetupAllowed: true`. Measured false on Keycloak 26.2.5 (§6.3's muutosmerkintä of 31.10.2026): on an
 * ALTERNATIVE execution that setting enrols nobody and the person is refused outright. The owner's remedy
 * made `auth-otp-form` REQUIRED in `CLMS 2FA methods` (task bddddbd2), and the same realm then serves the
 * TOTP setup page. What it does NOT do is put that factor in the token: the enrolment runs as the
 * `CONFIGURE_TOTP` required action, so the `auth-otp-form` execution never completes and the token minted
 * at that login still carries `amr: ["pwd"]` — §6's check refuses it exactly as before.
 *
 * So the way out is: refusal → this redirect → enrolment → a token that is still refused → this redirect
 * AGAIN → the OTP form, which the realm can now ask for → admitted. The latch below is per DOCUMENT LOAD
 * and the enrolment's own OIDC callback is a document load, which is what makes the second redirect
 * happen; a latch that outlived the page would leave such a person enrolled and still locked out. The
 * realstack spec's REFUSED / ENROLS case drives exactly that sequence.
 *
 * `tokenUsed` is the access token the refused request carried (`lib/api`'s `authHeaderWithToken`), and it
 * is what keeps that sequence to TWO redirects rather than three: a callback document boots on the
 * previous login's tokens while its own exchange is in flight, so a refusal can arrive for a token the
 * session has already replaced — see the guard at the top of the body. Undefined means "not asked about a
 * particular token", which is the bearer-less sessions, and those return just below anyway.
 */
let reauthenticating = false;

export async function reauthenticateForSecondFactor(tokenUsed?: string): Promise<void> {
  if (reauthenticating) return;

  // A REFUSAL OF A TOKEN THAT IS NO LONGER HELD IS NOT THIS SESSION'S REFUSAL, and nothing is done about
  // it. The OIDC callback boots the app on the PREVIOUS login's tokens while its own exchange is in
  // flight, so for a few dozen milliseconds every request leaves carrying a token that is about to be
  // replaced; the backend refuses those correctly, and by the time the 401 arrives the answer has already
  // been minted. Redirecting then is actively destructive rather than merely redundant — `beginStepUp`
  // writes a fresh PKCE verifier over the one this document came back with, so the exchange in flight can
  // no longer complete and the token it was about to deliver is lost.
  //
  // Measured on main, run 41334510350 (task 2625efef): the second re-authentication minted
  // `amr: ["pwd","otp"]` — ADR-0021 §6's way out, complete — and `POST /api/profile/language/reconcile`,
  // sent 48 ms earlier on the previous token, came back 401 and drove a THIRD re-authentication nobody
  // answered. The realstack REFUSED / ENROLS case sat on Keycloak's password form until it timed out.
  //
  // NOT the latch below, deliberately: nothing is spent here, because nothing was answered. A refusal of
  // the token actually held still re-authenticates — which is what the enrolment's own factorless token
  // needs (see the second redirect described above), and that one compares equal and falls through.
  // `tokenUsed` is undefined for the bearer-less sessions, which return below for their own reason.
  if (tokenUsed !== undefined && tokenUsed !== getAccessToken()) return;

  // No IdP credential to re-challenge — the dev/E2E placeholder sessions. The refusal cannot arise there
  // either (the dev realm composes the marker onto nothing, so the backend's check never fires), and the
  // honest thing on a session with no identity provider is to leave the error to the caller rather than
  // to pretend a factor was shown.
  if (!isSsoConfigured() || !getIdToken()) return;

  reauthenticating = true;
  const urlSlug = resolveTenantSlug();
  const tenant = urlSlug ?? selectedTenantSlug() ?? storedSession()?.tenantSlug ?? null;
  // The same question `performStepUp` asks, and for the same reason (ADR-0018 §6.3): a customer's own
  // person in a federated tenant needs the hint or they are asked for a realm password they never had,
  // and a consultant who reached that tenant by a membership must not have it or the customer's broker
  // refuses them.
  const idpHint = reachedByMembership(urlSlug) ? null : await fetchIdpHint(tenant);
  await beginStepUp({
    tenant,
    returnTo: `${window.location.pathname}${window.location.search}`,
    locale: i18n.language,
    idpHint,
  });
}
